import React from 'react';
import {
  LineChartOutlined,
  SlidersOutlined,
  StockOutlined,
  BankOutlined,
  DeploymentUnitOutlined,
  SafetyOutlined,
  PartitionOutlined,
  AimOutlined,
  ThunderboltOutlined,
  SyncOutlined,
  DollarOutlined,
  FallOutlined,
  WarningOutlined,
  PieChartOutlined,
  RadarChartOutlined,
  RetweetOutlined,
  RocketOutlined,
  DotChartOutlined,
  FunctionOutlined,
  ExperimentOutlined,
  ClockCircleOutlined,
  ArrowRightOutlined,
  LockOutlined,
  StarFilled,
} from '@ant-design/icons';

import type { Game } from '../games';
import { CATEGORY_BY_ID, DIFFICULTY_COLORS } from '../games';

const ICONS: Record<string, React.ComponentType> = {
  lineChart: LineChartOutlined,
  sliders: SlidersOutlined,
  stock: StockOutlined,
  bank: BankOutlined,
  deploymentUnit: DeploymentUnitOutlined,
  safety: SafetyOutlined,
  partition: PartitionOutlined,
  aim: AimOutlined,
  thunderbolt: ThunderboltOutlined,
  sync: SyncOutlined,
  dollar: DollarOutlined,
  fall: FallOutlined,
  warning: WarningOutlined,
  pieChart: PieChartOutlined,
  radarChart: RadarChartOutlined,
  retweet: RetweetOutlined,
  rocket: RocketOutlined,
  dotChart: DotChartOutlined,
  function: FunctionOutlined,
  experiment: ExperimentOutlined,
};

/** A card is playable when it has a native embed, or when it's a real
 *  Development-Plan game whose node is unlocked. Everything else — the legacy
 *  mock cards (no game behind them) and still-locked devplan games — renders
 *  grayed/inert with the same treatment (see `--locked` in puzzles.css). */
export const isPlayable = (game: Game): boolean =>
  !!game.embed || (game.backing === 'devplan' && game.status !== 'locked');

type Props = { game: Game; onLaunch: (g: Game) => void };

const Status: React.FC<{ game: Game; grayed: boolean }> = ({ game, grayed }) => {
  if (grayed)
    return (
      <span className="puzzles__status puzzles__status--locked">
        <LockOutlined /> Locked
      </span>
    );
  if (game.status === 'solved')
    return (
      <span className="puzzles__status puzzles__status--solved">
        ✓ Best {game.best}
      </span>
    );
  if (game.status === 'in-progress')
    return (
      <span className="puzzles__status puzzles__status--prog">
        Resume · {game.best}
      </span>
    );
  return (
    <span className="puzzles__play">
      Play <ArrowRightOutlined />
    </span>
  );
};

const GameCard: React.FC<Props> = ({ game, onLaunch }) => {
  const cat = game.category ? CATEGORY_BY_ID[game.category] : undefined;
  const catColor = cat?.color ?? '#5c6b7f';
  const catLabel = cat?.label ?? 'Development Plan';
  const diffColor = game.difficulty
    ? DIFFICULTY_COLORS[game.difficulty]
    : undefined;

  const playable = isPlayable(game);
  const grayed = !playable;
  const Icon = grayed ? LockOutlined : ICONS[game.icon] ?? AimOutlined;

  const cls =
    'puzzles__card' +
    (game.featured && playable ? ' puzzles__card--featured' : '') +
    (grayed ? ' puzzles__card--locked' : '');

  return (
    <button
      type="button"
      className={cls}
      style={{ ['--cat' as string]: catColor } as React.CSSProperties}
      onClick={() => onLaunch(game)}
      aria-label={`${game.title} — ${catLabel}${
        game.difficulty ? `, ${game.difficulty}` : ''
      }`}
    >
      <div className="puzzles__card-top">
        <span
          className="puzzles__chip"
          style={{
            background: grayed ? 'rgba(92,107,127,0.12)' : `${catColor}1A`,
            color: grayed ? '#5c6b7f' : catColor,
          }}
        >
          <Icon />
        </span>
        {diffColor && game.difficulty && (
          <span
            className="puzzles__diff"
            style={{ background: `${diffColor}14`, color: diffColor }}
          >
            {game.difficulty}
          </span>
        )}
      </div>

      <span
        className="puzzles__cat"
        style={{ color: game.featured && playable ? '#d48806' : catColor }}
      >
        {game.featured && playable ? (
          <>
            <StarFilled /> Featured
          </>
        ) : (
          catLabel
        )}
      </span>

      <h3 className="puzzles__card-title">{game.title}</h3>
      {game.blurb && <p className="puzzles__card-blurb">{game.blurb}</p>}

      <div className="puzzles__card-foot">
        {game.minutes != null ? (
          <span className="puzzles__meta">
            <ClockCircleOutlined /> {game.minutes} min
            {game.skills && (
              <>
                <span className="puzzles__dot-sep">·</span>
                {game.skills}
              </>
            )}
          </span>
        ) : (
          <span className="puzzles__meta" />
        )}
        <Status game={game} grayed={grayed} />
      </div>
    </button>
  );
};

export default GameCard;
